/**
 * **THE currency module.** Every currency concern lives here: the symbol table, the code this
 * terminal is configured with, the symbol a screen prints, and the precision and grouping it
 * prints them at. It replaced `utils/currencyConstants` plus two divergent `helpers/commonHelpers`
 * bodies; those now re-export from here, so this is the one DEFINITION.
 *
 * Pure apart from three store reads — `currency()`, `amountDecimals()`, `amountLocale()` — so the
 * interesting part (code -> symbol) stays testable with plain strings. That store import is why
 * `./format` is a separate file; see its header.
 *
 * On the KIOSK nothing calls these directly except the shell: `KioskApp` resolves them once and
 * provides them, and every price reads `useKioskMoney()`. See docs/architecture/KIOSK.md.
 */
import common from "@dotpe/rista-library/lib/common.js";
import store from "../../redux-store";
import {
  CURRENCY_CONSTANTS,
  DEFAULT_AMOUNT_DECIMALS,
  DEFAULT_AMOUNT_LOCALE,
  INR,
  formatAmount,
  formatMoney,
} from "./format";
import type { AmountSpec, MoneyFormat, MoneyOptions } from "./format";

/**
 * `@rista/core/utils/currency` answers everything `./format` does, so the split is invisible to
 * importers. A star re-export on purpose: the hand-written list this replaced named eight symbols
 * that had to stay in sync with the file next door by memory.
 *
 * ⚠️ Names imported ABOVE are listed there too, and deliberately: a re-export creates no local
 * binding (ledger rule 5). `amountDecimalsFor` is re-exported but not imported — nothing here
 * calls it.
 */
export * from "./format";

/**
 * `&#x20b9` -> `₹`. PURE, so it runs on both platforms.
 *
 * 🔴 It replaces a `document.createElement("textarea")` decoder that only web could have, and
 * that is the whole reason `currency()` used to be two functions. The table it decodes is
 * rista-library's `common.curr` — 118 entries, EVERY one a sequence of hex character references
 * with no trailing semicolon (`&#x4c&#x65&#x6b` = "Lek"), which is why this handles hex and the
 * optional semicolon and nothing else. A named or decimal entity would pass through unchanged
 * rather than throw; nothing in the table is one. Verified against the installed 4.0.1, and
 * `currency.test.ts` re-verifies it over every entry rather than trusting this note.
 *
 * Anything without an entity in it — `CURRENCY_CONSTANTS[INR]` is the literal "₹" — comes back
 * untouched, which is what makes it safe to run over the fallbacks too.
 */
const decodeHtmlEntities = (value: string): string =>
  value
    ? value.replace(/&#x([0-9a-fA-F]+);?/g, (_, hex: string) =>
        String.fromCodePoint(parseInt(hex, 16)),
      )
    : "";

/**
 * The symbol to print for a currency CODE. Pure — the store read is in `currency()`.
 *
 * Three answers, in order: the library's symbol for the code, the code itself (an unknown
 * currency prints as "XYZ 100" — wrong-looking, but honest), and finally INR.
 *
 * ⚠️ The library is IMPORTED, never read off `window.RL` (CLAUDE.md / audit check K). The web app
 * file used to read `window?.RL?.common?.curr` — that global IS this module, installed by the web
 * bundle's `rista-library-babel.js` script, so this is the same table with no bootstrap
 * dependency, and mobile reaches it the same way.
 */
export function currencySymbolFor(currencyCode?: string): string {
  return decodeHtmlEntities(
    (currencyCode && common?.curr?.[currencyCode]) || currencyCode || CURRENCY_CONSTANTS[INR],
  );
}

/**
 * The currency symbol for the business this terminal is bound to.
 *
 * 🔴 **ONE function since 2026-09-18, and the unification is a mobile BUG FIX.** It was two
 * functions with one name: web resolved the code through rista-library's 118-currency table,
 * mobile indexed `CURRENCY_CONSTANTS` — a map with exactly ONE key. So every non-INR business
 * printed `undefined` on mobile (`undefined1,240`), and the INR fallback hid it everywhere the
 * merchant happened to be Indian. Web's behaviour is the one kept, per the standing rule that web
 * is the source of truth for display.
 *
 * It had to become one function anyway: `packages/ui/components/CustomerConsent`,
 * `loyalty/hooks/useLoyaltyRedemption` and `kiosk/payment/hooks/useKioskPayment` are all SHARED
 * modules that call it, so both platforms were already running the same callers over two
 * different bodies.
 *
 * Reads the store INSIDE the body, never at module scope — a module-eval-time read breaks the
 * bundle nondeterministically through core's import cycles (`pnpm audit:uplift` checks H/I/J).
 */
export const currency = (): string =>
  currencySymbolFor(store.getState().businessStore?.businessData?.currencyCode);

/* ── how this merchant's money LOOKS ───────────────────────────────────────────────────────── */

/**
 * 🔴 **THE TWO LINES THE MERCHANT CONFIG LANDS ON.** Both answer a default today, because neither
 * setting exists yet — nothing on `businessData` carries a precision or a locale. The day the
 * backend ships them:
 *
 * ```ts
 * export const amountDecimals = (): number =>
 *   amountDecimalsFor(store.getState().businessStore?.businessData?.currencyDecimalPlaces);
 * export const amountLocale = (): string =>
 *   store.getState().businessStore?.businessData?.numberLocale || DEFAULT_AMOUNT_LOCALE;
 * ```
 *
 * and every price in the POS, the kiosk and the customer display follows — which is the whole
 * point of routing amounts through `money()` first. Three rules for that edit:
 *
 *   · the read sits INSIDE the body, never at module scope (`pnpm audit:uplift` checks H/I/J);
 *   · precision goes through `amountDecimalsFor`, so a bad value off the wire cannot reach
 *     `toFixed` — and NOT through `||`, which would turn a configured `0` into two. The locale is
 *     the opposite: an empty string is not a meaningful value, so `||` is right there;
 *   · ⚠️ both field names are guesses at the contract — confirm before wiring.
 *     `currencyDecimalPlaces`, not `decimalPlaces`, because its neighbours on `businessData` are
 *     `roundingMode` and `enableRoundingCashOnly`, which are TRANSACTION rounding.
 *
 * 🔴 **DISPLAY ONLY. Neither may reach the money MATHS.** `roundOffTwoDecimal`, the four `round2`
 * bodies and `@rista/payments`' `money/legs.ts` decide what is CHARGED and SENT; they stay on two
 * decimals whatever these return. Wire one in and the day a merchant is configured for three, the
 * amount charged stops matching the amount quoted.
 */
export const amountDecimals = (): number => DEFAULT_AMOUNT_DECIMALS;

/** See `amountDecimals` above — the same seam, for grouping rather than precision. */
export const amountLocale = (): string => DEFAULT_AMOUNT_LOCALE;

/**
 * What `amount()` and `money()` both have to work out before they can format: this merchant's
 * precision and grouping, with the call site's overrides applied.
 *
 * 🔴 **It is a function so the two cannot DRIFT.** The subtlety is `options?.grouping === false`
 * — never a truthiness test, because `undefined` means "the merchant's" and only an explicit
 * `false` opts out. Written twice, one copy eventually gets "simplified" to `?` or `||` and
 * that surface silently stops grouping. Five app test stubs shipped exactly that mistake
 * (`options?.grouping ? "en-IN" : undefined`) and it went unnoticed for months.
 *
 * Precision is `??` for the mirror-image reason: a per-call `0` is a real answer, and `||`
 * would read it as unset.
 */
const resolveFormat = (options?: MoneyOptions): AmountSpec => ({
  decimals: options?.decimals ?? amountDecimals(),
  grouping: options?.grouping === false ? (false as const) : amountLocale(),
});

/**
 * An amount's digits for THIS business, no symbol: `amount(1234.5)` -> `"1,234.50"`.
 *
 * Takes the SAME `options` as `money()`, and deliberately so — it is that function without the
 * symbol, so every reason a call site needs `{ decimals: 0 }` or `{ grouping: false }` applies
 * here unchanged. It went without them until 2026-09-19 purely by oversight.
 *
 * 🔴 **Not typed `MoneyFormat`, though it satisfies the shape.** That type is the contract for a
 * COMPLETE money string, and components take it precisely so a symbol cannot go missing. Typing
 * this as one would let it be passed wherever `money` is expected, and a panel would quietly
 * render `1,234.50` with nothing in front of it.
 *
 * ⚠️ **No caller yet.** It is the half of the `amount()` / `money()` pair for screens that print
 * the symbol in their own styled node — web's cart kerns it with `tracking-tighter`, so `money()`
 * would change their DOM. Those are the ~100 legacy `toFixed(2)` sites in `apps/*`, none migrated.
 *
 * ⚠️ `amount` is a common local variable name; a file that already has one aliases the import —
 * a compile error, never a silent bug.
 */
export const amount = (value: unknown, options?: MoneyOptions): string =>
  formatAmount(value, resolveFormat(options));

/**
 * A complete money string for THIS business: `money(1234.5)` -> `"₹1234.50"`.
 *
 * Symbol and precision resolved together, which is what a call site printing one string actually
 * wants. Bare noun = store-resolved, `format*` = pure and explicit — the same rule as
 * `currency()` / `currencySymbolFor()`.
 *
 * `options` is for the two things a call site legitimately decides for ITSELF:
 *
 * ```ts
 * money(120000)                      // "₹1,20,000.00" — grouped, the everyday call
 * money(100, { decimals: 0 })         // "₹100"         — a rate base: "per ₹100 spent"
 * money(120000, { grouping: false })  // "₹120000.00"   — opt out for one figure
 * ```
 *
 * 🔴 **What it deliberately does NOT take is a symbol or a locale.** Those are the merchant's,
 * resolved here from one slice at one moment. A call site that could choose them is a call site
 * that can disagree with the bill beside it — which is the whole reason components take this
 * function rather than a `currencySymbol` prop.
 *
 * ⚠️ **Never feed this back into itself.** A grouped result is not a number: `formatAmount`
 * refuses grouped strings by design, so `money(money(1200))` is `"₹0.00"`.
 */
export const money: MoneyFormat = (value, options) =>
  formatMoney(value, currency(), resolveFormat(options));
